// coteObiect3d.ts — cotele PIESEI ALESE din scena 3D.
//
// `cote3d.ts` cotează CLĂDIREA: gabaritul, înălțimile de etaj, cotele de
// nivel. Aici se cotează UN SINGUR element, cel pe care tocmai l-ai ales cu
// clickul — stâlp, grindă sau ax de grindă de fundare —, fiindcă întrebările
// pe care le pui după ce ai ales o piesă sunt alte două:
//
//   1. «cât e de mare?» — secțiunea și lungimea ei, citite din desen, nu
//      dintr-un panou lateral;
//   2. «cât e până la ce e lângă ea?» — lumina până la piesa vecină, în
//      cele patru direcții din planul ei.
//
// Amândouă se măsoară din GEOMETRIA DESENATĂ, nu din cifrele modelului: ce
// se cotează e exact ce se vede. Un stâlp în L nu are un «b×h», dar are o
// cutie; un ax de fundație e desenat din talpă și inimă, iar fiecare își
// primește secțiunea lui.
//
// REPERUL. Toate măsurile sunt în sistemul PIESEI, nu în cel global: `u` e
// direcția ei (peretele stâlpului, deschiderea grinzii, axul fundației), `v`
// e transversala, iar `w` e verticala. Pe o clădire rotită cu 27°, cotele
// scrise pe axele globale ar da lățimi și lungimi care nu există nicăieri.
//
// CE SE COTEAZĂ ÎN JUR. Nu «cel mai apropiat obiect», care pe o diagonală e
// aproape întotdeauna ceva ce nu stă în fața piesei, ci tot ce ÎNFRUNTĂ o
// FAȚĂ a ei: se ridică perpendiculare din fiecare punct al feței, iar fiecare
// piesă la care ajunge vreuna dintre ele primește cota ei. Nu doar din
// mijlocul feței — un stâlp prins de colțul peretelui e în fața lui pe zece
// centimetri, și cei zece centimetri sunt tot un drum liber —, și nu doar una
// pe direcție: o față lungă poate avea, în dreptul unei porțiuni, un stâlp la
// 2 m și, în dreptul alteia, peretele de peste cameră la 5 m. Ce stă în
// SPATELE unei piese mai apropiate nu se cotează: acolo perpendiculara s-a
// oprit deja. Piesele care se ating (sub un centimetru) nu primesc cotă —
// acolo nu e lumină, e îmbinare — dar ascund, ca oricare alta.
//
// TOATE cele șase direcții ale piesei, nu doar cele patru din plan: sub o
// grindă e înălțimea liberă până la planșeul de dedesubt, iar aceea e aceeași
// întrebare pusă pe verticală.
//
// Stilul e al planșei, împrumutat întreg din `hud3d.ts`: săgeți pline,
// martori peste linia de cotă, valoarea în planul cotei. Cotele PIESEI sunt
// în cerneală, distanțele până la vecine în gri: primele spun ce e piesa, a
// doua unde stă.

import * as THREE from "three";
import { PAL } from "./paletaDesen";
import {
  cotaLiniara, materialLinie, materialMartor, materialSageata, mFmt,
  segmente, textCota, triunghiuri,
} from "./hud3d";

type V3 = [number, number, number];

/** Cutia unei piese în reperul piesei alese. */
export interface CutieLocala {
  umin: number; umax: number;
  vmin: number; vmax: number;
  zmin: number; zmax: number;
}

/** Sub un centimetru două piese nu au lumină între ele: se ating. */
const ATINGERE_M = 0.01;
/** Câte secțiuni distincte se cotează cel mult (un ax de fundație cu multe
 *  tronsoane ar scrie altfel zeci de cote identice una peste alta). */
const MAX_SECTIUNI = 4;
/** Câte MĂSURI DISTINCTE se scriu cel mult pe o direcție. Fața unui perete
 *  lung poate înfrunta zece piese diferite; peste patru cote pe aceeași
 *  direcție desenul nu mai e citibil.
 *
 *  Se păstrează cele mai APROPIATE. Ar fi părut mai deștept să se păstreze
 *  piesele care înfruntă cea mai mare parte din față — și e fals: fața rămasă
 *  liberă printre vecinele apropiate e mare, iar prin ea se vede departe, așa
 *  că regula aceea scotea vecina de la 2 m ca să scrie 43 m până în capătul
 *  celălalt al clădirii. Adevărat, și fără folos: întrebarea pusă de un click
 *  pe o piesă e ce e LÂNGĂ ea. */
const MAX_LUMINI = 4;
/** Câte piese se cercetează cel mult pe o direcție. În mod normal căutarea se
 *  oprește singură — patru măsuri scrise, sau fața acoperită toată —, iar
 *  plafonul ăsta e pentru fața care nu se acoperă niciodată: colțul clădirii,
 *  care vede până afară printre toți stâlpii de pe drum. */
const MAX_PRIVITE = 512;
/** Cât trebuie să fie de LARGĂ o fantă ca perpendiculara să treacă prin ea.
 *
 *  O LĂȚIME, nu o arie, și ăsta e tot rostul pragului. Stâlpii de pe același
 *  ax nu sunt perfect aliniați — un grad de rotație, un centimetru de
 *  decalaj —, iar umbra celui din față lasă pe lângă ea o fâșie de câțiva
 *  milimetri, dar lungă cât etajul: arie mare, fantă inexistentă. Prin ea se
 *  vedea stâlpul DE DUPĂ, și se cota până la el, peste cel care îl acoperă.
 *
 *  Se cer deci 5 cm pe AMÂNDOUĂ laturile. Pragul e și cel prin care o piesă
 *  intră în discuție: un stâlp prins de colțul peretelui pe 10 cm rămâne o
 *  vecină adevărată, unul prins pe 3 mm nu.
 *
 *  La o piesă mai subțire decât pragul, pragul devine grosimea ei: altfel un
 *  perete de 4 cm n-ar avea niciodată nimic în față. */
const FANTA_M = 0.05;

/** Axele reperului piesei: `u` în lungul ei, `v` transversal, `z` vertical. */
type Ax = "u" | "v" | "z";

/** Cele două axe TRANSVERSALE unei direcții — planul feței din care se ridică
 *  perpendicularele. */
const TRANSVERSALE: Record<Ax, [Ax, Ax]> = {
  u: ["v", "z"], v: ["u", "z"], z: ["u", "v"],
};

/** Intervalul unei cutii pe o axă. */
const pe = (c: CutieLocala, a: Ax): [number, number] =>
  a === "u" ? [c.umin, c.umax]
    : a === "v" ? [c.vmin, c.vmax] : [c.zmin, c.zmax];

const lung = (c: CutieLocala, ax: "u" | "v" | "z") =>
  ax === "u" ? c.umax - c.umin : ax === "v" ? c.vmax - c.vmin : c.zmax - c.zmin;

/** Cutia unui mesh, măsurată în reperul (u, v, z).
 *
 *  Din VÂRFURILE geometriei, nu din cutia ei. Cutia geometriei e aliniată la
 *  axele lumii, iar proiectată în reperul piesei dă cutia unei cutii: pentru
 *  un zid de 0,30 m desenat prin extrudare în coordonatele lumii, pe o
 *  clădire rotită cu 27°, ieșea o grosime de un metru — și, cu ea, cote care
 *  porneau de la un metru în afara zidului. Din vârfuri, rezultatul e exact
 *  pentru orice formă: cutia dreaptă care o cuprinde, în reperul ei.
 *
 *  La o formă neconvexă — un stâlp în L, un zid în T — rămâne tot cutia care
 *  o cuprinde, fiindcă asta se și cotează: golul dintre brațele L-ului nu e o
 *  măsură a piesei.
 *
 *  Piesa desenată MAI SCURTĂ decât e — stâlpul oprit la fața peretelui în
 *  care intră — își poartă forma întreagă în `userData.geoCote`: cota spune
 *  cât e stâlpul, nu cât se vede din el pe lângă perete. */
export function cutiaLocala(mesh: THREE.Mesh, u: THREE.Vector3,
                            v: THREE.Vector3): CutieLocala | null {
  const g = (mesh.userData?.geoCote as THREE.BufferGeometry | undefined)
    ?? mesh.geometry;
  const poz = g?.getAttribute?.("position");
  if (!poz || !poz.count) return null;
  mesh.updateWorldMatrix(true, false);
  const p = new THREE.Vector3();
  let c: CutieLocala | null = null;
  for (let i = 0; i < poz.count; i++) {
    p.fromBufferAttribute(poz as THREE.BufferAttribute, i)
      .applyMatrix4(mesh.matrixWorld);
    const a = p.x * u.x + p.y * u.y;
    const b = p.x * v.x + p.y * v.y;
    if (!c) {
      c = { umin: a, umax: a, vmin: b, vmax: b, zmin: p.z, zmax: p.z };
    } else {
      if (a < c.umin) c.umin = a; else if (a > c.umax) c.umax = a;
      if (b < c.vmin) c.vmin = b; else if (b > c.vmax) c.vmax = b;
      if (p.z < c.zmin) c.zmin = p.z; else if (p.z > c.zmax) c.zmax = p.z;
    }
  }
  return c;
}

function reuniune(cutii: CutieLocala[]): CutieLocala {
  return cutii.reduce((a, c) => ({
    umin: Math.min(a.umin, c.umin), umax: Math.max(a.umax, c.umax),
    vmin: Math.min(a.vmin, c.vmin), vmax: Math.max(a.vmax, c.vmax),
    zmin: Math.min(a.zmin, c.zmin), zmax: Math.max(a.zmax, c.zmax),
  }));
}

/** Un dreptunghi din planul unei fețe, în coordonatele celor două axe
 *  transversale direcției măsurate. */
interface Dreptunghi { p1: number; p2: number; q1: number; q2: number; }

/** Marginile care cad în interiorul unui interval, plus capetele lui: cu ele
 *  un dreptunghi se taie în celule peste care umbrele sunt sau nu sunt. */
function taieturi(lo: number, hi: number, margini: number[]): number[] {
  const t = new Set<number>([lo, hi]);
  margini.forEach((m) => { if (m > lo && m < hi) t.add(m); });
  return [...t].sort((a, b) => a - b);
}

/** Ce rămâne descoperit din `d` după ce `umbre` — piesele mai apropiate — i-au
 *  luat fața: punctul din cea mai mare fereastră liberă care e și destul de
 *  largă ca să treacă ceva prin ea (v. `FANTA_M`). Acolo se scrie cota, și
 *  tot acolo se vede că piesa chiar e în față.
 *
 *  `null` = fața e acoperită, deci nicio perpendiculară nu ajunge la piesă.
 *
 *  Dreptunghiurile nu sunt disjuncte, deci nu se poate scădea nimic: se taie
 *  dreptunghiul cu marginile umbrelor și se întreabă fiecare celulă, o
 *  singură dată, dacă e acoperită. Numărul de umbre e mic — căutarea se
 *  oprește de îndată ce fața s-a acoperit toată —, deci grila rămâne mică. */
function fantaLibera(d: Dreptunghi, umbre: Dreptunghi[],
                     pragP: number, pragQ: number):
    { p: number; q: number } | null {
  const rel = umbre.filter((s) =>
    s.p1 < d.p2 && s.p2 > d.p1 && s.q1 < d.q2 && s.q2 > d.q1);
  if (!rel.length) return { p: (d.p1 + d.p2) / 2, q: (d.q1 + d.q2) / 2 };
  const ps = taieturi(d.p1, d.p2, rel.flatMap((s) => [s.p1, s.p2]));
  const qs = taieturi(d.q1, d.q2, rel.flatMap((s) => [s.q1, s.q2]));
  let unde: { p: number; q: number } | null = null;
  let ceaMaiMare = 0;
  for (let i = 0; i + 1 < ps.length; i++) {
    const lp = ps[i + 1] - ps[i];
    if (lp < pragP) continue;
    for (let j = 0; j + 1 < qs.length; j++) {
      const lq = qs[j + 1] - qs[j];
      if (lq < pragQ) continue;
      const cp = (ps[i] + ps[i + 1]) / 2;
      const cq = (qs[j] + qs[j + 1]) / 2;
      if (rel.some((s) => s.p1 < cp && cp < s.p2 && s.q1 < cq && cq < s.q2))
        continue;
      if (lp * lq > ceaMaiMare) { ceaMaiMare = lp * lq; unde = { p: cp, q: cq }; }
    }
  }
  return unde;
}

/** O cotă de lumină: cât e până la piesa găsită și în dreptul cărui punct al
 *  feței se scrie. */
export interface Lumina {
  lumina: number;
  /** coordonatele pe cele două axe transversale direcției */
  p: number;
  q: number;
}

/** Tot ce înfruntă o FAȚĂ a piesei pe direcția cerută, de la apropiat la
 *  depărtat.
 *
 *  O piesă intră în listă dacă o perpendiculară ridicată din vreun punct al
 *  feței ajunge la ea — adică dacă umbrele lor pe planul feței se suprapun pe
 *  o bucată cât de mică — și dacă nicio piesă mai apropiată nu acoperă toată
 *  bucata aceea. Piesele lipite nu primesc cotă, dar umbresc: altfel s-ar
 *  cota prin ele. */
export function luminiDinDirectie(
  sel: CutieLocala, vecini: CutieLocala[], ax: Ax, semn: 1 | -1,
): Lumina[] {
  const [axP, axQ] = TRANSVERSALE[ax];
  const [amin, amax] = pe(sel, ax);
  const fata = semn > 0 ? amax : amin;
  const [p1, p2] = pe(sel, axP);
  const [q1, q2] = pe(sel, axQ);
  const FATA: Dreptunghi = { p1, p2, q1, q2 };
  // pragul de fantă, dar niciodată mai mare decât fața însăși
  const pragP = Math.min(FANTA_M, p2 - p1);
  const pragQ = Math.min(FANTA_M, q2 - q1);

  const candidate: (Dreptunghi & { lumina: number })[] = [];
  vecini.forEach((n) => {
    const [np1, np2] = pe(n, axP);
    const [nq1, nq2] = pe(n, axQ);
    // fereastra prin care se văd cele două piese, privite din direcția
    // măsurată: goală = nicio perpendiculară a feței nu dă de piesă.
    //
    // Se cer AMBELE laturi destul de late, fiecare pe cont propriu, nu aria:
    // o piesă care ratează fața și pe o transversală, și pe cealaltă — cea de
    // pe diagonală — dă două suprapuneri negative, iar produsul lor e
    // pozitiv. Judecată după arie, ea ar fi trecut drept vecina cea mai
    // apropiată, cu o fereastră întoarsă pe dos, iar cota s-ar fi scris în
    // aer, între cele două piese.
    const d: Dreptunghi = {
      p1: Math.max(p1, np1), p2: Math.min(p2, np2),
      q1: Math.max(q1, nq1), q2: Math.min(q2, nq2),
    };
    if (d.p2 - d.p1 < pragP || d.q2 - d.q1 < pragQ) return;
    const [nmin, nmax] = pe(n, ax);
    const lumina = semn > 0 ? nmin - fata : fata - nmax;
    // CÂT AJUNGE PIESA DINCOLO DE FAȚĂ. Nu e același lucru cu lumina, iar
    // confuzia costa: o piesă lipită de față, sau întrepătrunsă cu ea, are
    // lumina zero sau negativă și era aruncată din discuție cu totul — deci
    // nu umbrea nimic, iar cota sărea peste ea, până la următoarea. Peretele
    // de zidărie al etajului de deasupra stă exact pe al nostru: aruncat, el
    // lăsa să se scrie «3,00 m» până la cel de peste două etaje.
    //
    // Piesa care stă TOATĂ în spatele feței nu e în față și se sare; cea care
    // o depășește, oricât de puțin, e în față — la distanța zero dacă o
    // atinge. Așa intră în umbre, fără să primească o cotă (v. `ATINGERE_M`).
    const capat = semn > 0 ? nmax - fata : fata - nmin;
    if (capat <= 0) return;                   // toată în spatele feței
    candidate.push({ ...d, lumina: Math.max(0, lumina) });
  });
  candidate.sort((a, b) => a.lumina - b.lumina);

  // O MĂSURĂ SE SCRIE O SINGURĂ DATĂ, și pe direcția asta. Piesele care se
  // opresc în același plan sunt multe și obișnuite — stâlpul următor și cele
  // trei grinzi care intră în el au toate aceeași față —, iar patru cote cu
  // «0,98 m» una lângă alta nu spun nimic în plus și mănâncă locul măsurilor
  // care chiar sunt altele.
  //
  // Granița e CE SE SCRIE, nu cât e în model: trei piese la 1,100, 1,101 și
  // 1,102 m sunt trei distanțe diferite și o singură cotă, fiindcă toate trei
  // se scriu «1,10 m». Cheia e deci chiar textul — dacă se schimbă vreodată
  // numărul de zecimale al planșei, se schimbă odată cu el și ce se consideră
  // repetat.
  const gasite: Lumina[] = [];
  const scrise = new Set<string>();
  const umbre: Dreptunghi[] = [];
  let privite = 0;
  for (const c of candidate) {
    if (scrise.size >= MAX_LUMINI || privite++ >= MAX_PRIVITE) break;
    const unde = fantaLibera(c, umbre, pragP, pragQ);
    umbre.push(c);
    if (!unde) continue;                      // ascunsă de una mai apropiată
    const scris = mFmt(c.lumina);
    if (c.lumina >= ATINGERE_M && !scrise.has(scris)) {
      scrise.add(scris);
      gasite.push({ lumina: c.lumina, p: unde.p, q: unde.q });
    }
    // fața s-a acoperit toată: ce urmează e sigur în spatele a ceva
    if (!fantaLibera(FATA, umbre, pragP, pragQ)) break;
  }
  return gasite;
}

/** Cotele piesei alese, ca grup 3D.
 *
 *  `alese` sunt mesh-urile ei (un ax de fundație are talpa și inima fiecărui
 *  tronson), `vecini` piesele față de care se măsoară lumina — deja filtrate
 *  de cine le cheamă: vizibile, structurale, fără piesa aleasă —, iar
 *  `unghi` e direcția piesei în plan (radiani). */
export function grupCoteObiect(alese: THREE.Mesh[], vecini: THREE.Mesh[],
                               unghi: number): THREE.Group {
  const grup = new THREE.Group();
  grup.userData.adnotare = true;
  const u = new THREE.Vector3(Math.cos(unghi), Math.sin(unghi), 0);
  const v = new THREE.Vector3(-Math.sin(unghi), Math.cos(unghi), 0);

  const piese = alese
    .map((m) => cutiaLocala(m, u, v))
    .filter((c): c is CutieLocala => !!c);
  if (!piese.length) return grup;
  const P = reuniune(piese);

  // Mărimile desenului sunt ale PIESEI, nu ale clădirii: cine alege un stâlp
  // se uită la el de aproape, iar cotele scrise la scara blocului i-ar
  // acoperi tot ecranul. Pragul de jos ține cota citibilă pe o inimă de
  // 25 cm, cel de sus o împiedică să plece în câmp de la un ax de fundație.
  const gros = Math.min(lung(P, "u"), lung(P, "v"));
  const pas = Math.min(1.2, Math.max(0.3, gros * 0.6));
  const text = Math.max(0.14, pas * 0.45);
  const sageata = Math.max(0.07, pas * 0.2);

  const linii: number[] = [];
  const martori: number[] = [];
  const sageti: number[] = [];
  const liniiMut: number[] = [];
  const martoriMut: number[] = [];
  const sagetiMut: number[] = [];
  /** Un punct din reperul piesei, în lume. */
  const pct = (a: number, b: number, z: number): V3 =>
    [u.x * a + v.x * b, u.y * a + v.y * b, z];
  /** SENSUL în care se scrie o cotă. Nu cel în care s-a măsurat: o lumină
   *  luată spre −u e aceeași cotă ca una spre +u, iar una măsurată în jos nu
   *  se citește de sus în jos. Fără regula asta, jumătate din cotele unei
   *  piese ieșeau întoarse — cele pe direcțiile negative —, iar cele
   *  verticale măsurate în jos, cu capul în jos de-a binelea.
   *
   *  Canonic: pe verticală mereu în sus, în plan mereu în sensul piesei. */
  const sensScris = (d: THREE.Vector3): THREE.Vector3 => {
    if (Math.abs(d.z) > 0.5) return d.z < 0 ? d.clone().negate() : d;
    const pe_u = d.dot(u);
    const intors = Math.abs(pe_u) > 1e-6 ? pe_u < 0 : d.dot(v) < 0;
    return intors ? d.clone().negate() : d;
  };
  const cota = (a: V3, b: V3, dep: V3, valoare: string, mut = false,
                normala?: THREE.Vector3) => {
    const c = cotaLiniara(a, b, dep, sageata, normala);
    (mut ? liniiMut : linii).push(...c.linii);
    (mut ? martoriMut : martori).push(...c.martori);
    (mut ? sagetiMut : sageti).push(...c.sageti);
    const t = textCota(valoare, text, c.mijloc, sensScris(c.dir), c.nrm,
                       mut ? PAL.mut : PAL.ink);
    if (t) grup.add(t);
    return c;
  };

  // --- 1. DIMENSIUNILE PIESEI ---------------------------------------------
  // Planul cotelor de plan e talpa piesei PRINCIPALE (cea mai voluminoasă):
  // la un stâlp de parter, care e desenat și cu bucata coborâtă în elevație,
  // cota de lungime pusă pe cutia reunită ar cădea în fundație.
  const volum = (c: CutieLocala) =>
    lung(c, "u") * lung(c, "v") * lung(c, "z");
  const principala = piese.reduce((a, c) => (volum(c) > volum(a) ? c : a));
  const zPlan = principala.zmin;

  // lungimea, în lungul piesei, pe talpa ei
  cota(pct(P.umin, P.vmin, zPlan), pct(P.umax, P.vmin, zPlan),
       [-v.x * pas, -v.y * pas, 0], `${mFmt(lung(P, "u"))} m`);

  // Secțiunile: o lățime și o înălțime pentru fiecare piesă DISTINCTĂ. Talpa
  // și inima unei fundații sunt două secțiuni, dar cele patru tronsoane ale
  // aceluiași ax sunt una singură — cheia e măsura, nu piesa.
  const vazute = new Set<string>();
  const cheie = (a: number, b: number) =>
    `${Math.round(a * 1000)}:${Math.round(b * 1000)}`;
  let nSectiuni = 0;
  piese
    .slice()
    .sort((a, b) => a.zmin - b.zmin)
    .forEach((p) => {
      if (nSectiuni >= MAX_SECTIUNI) return;
      const kL = `L${cheie(p.vmin, p.vmax)}`;
      const kH = `H${cheie(p.zmin, p.zmax)}`;
      if (vazute.has(kL) && vazute.has(kH)) return;
      nSectiuni++;
      if (!vazute.has(kL)) {
        vazute.add(kL);
        // lățimea, la capătul piesei: acolo se vede secțiunea
        cota(pct(P.umax, p.vmin, p.zmin), pct(P.umax, p.vmax, p.zmin),
             [u.x * pas, u.y * pas, 0], `${mFmt(lung(p, "v"))} m`);
      }
      if (!vazute.has(kH)) {
        vazute.add(kH);
        // înălțimea, pe muchia din colțul opus; piesele suprapuse (stâlpul și
        // bucata lui din elevație, talpa și inima) ies un lanț vertical
        const d = pas * 0.7;
        const dep: V3 = [(-u.x + v.x) * d, (-u.y + v.y) * d, 0];
        cota(pct(P.umin, P.vmax, p.zmin), pct(P.umin, P.vmax, p.zmax), dep,
             `${mFmt(lung(p, "z"))} m`);
      }
    });

  // --- 2. LUMINA PÂNĂ LA PIESELE DIN JUR ----------------------------------
  const cutiiVecine = vecini
    .map((m) => cutiaLocala(m, u, v))
    .filter((c): c is CutieLocala => !!c);
  const tick = sageata * 0.9;
  /** Un punct dat prin coordonata pe axa măsurată și cele două transversale,
   *  în ordinea din `TRANSVERSALE`. */
  const punctPe = (ax: Ax, a: number, p: number, q: number): V3 =>
    ax === "u" ? pct(a, p, q) : ax === "v" ? pct(p, a, q) : pct(p, q, a);
  (["u", "v", "z"] as const).forEach((ax) => {
    ([1, -1] as const).forEach((semn) => {
      const [amin, amax] = pe(P, ax);
      const fata = semn > 0 ? amax : amin;
      luminiDinDirectie(P, cutiiVecine, ax, semn).forEach((g) => {
        const A = punctPe(ax, fata, g.p, g.q);
        const B = punctPe(ax, fata + semn * g.lumina, g.p, g.q);
        // cota stă CHIAR în lumina măsurată, nu deplasată în afară: ea e a
        // golului dintre piese, iar golul e locul unde se citește.
        //
        // Nefiind deplasată, ea nu-și poate lua normala din deplasare, iar pe
        // verticală nici din «sus» — acolo e chiar direcția ei. I se dă
        // transversala piesei, ca scrisul să stea în planul ei, ca la o
        // elevație (v. `cotaLiniara`).
        cota(A, B, [0, 0, 0], `${mFmt(g.lumina)} m`, true,
             ax === "z" ? v : undefined);
        // …cu martori la capete, ca o cotă fără deplasare să-și arate totuși
        // muchiile. Transversali pe ea: verticali la cotele din plan,
        // orizontali la cele pe înălțime.
        const t: V3 = ax === "z"
          ? [u.x * tick, u.y * tick, 0]
          : [0, 0, tick];
        [A, B].forEach((Q) => {
          martoriMut.push(Q[0] - t[0], Q[1] - t[1], Q[2] - t[2],
                          Q[0] + t[0], Q[1] + t[1], Q[2] + t[2]);
        });
      });
    });
  });

  if (linii.length) grup.add(segmente(linii, materialLinie(PAL.ink)));
  if (sageti.length) grup.add(triunghiuri(sageti, materialSageata(PAL.ink)));
  if (martori.length)
    grup.add(segmente(martori, materialMartor(PAL.mut), true));
  if (liniiMut.length) grup.add(segmente(liniiMut, materialLinie(PAL.mut)));
  if (sagetiMut.length)
    grup.add(triunghiuri(sagetiMut, materialSageata(PAL.mut)));
  if (martoriMut.length) grup.add(segmente(martoriMut, materialMartor(PAL.mut)));

  // Ultimele desenate din toată scena. Cotele au `depthTest: false`, dar în
  // layerul «Realist» betonul e translucid și are ordine de desenare mare
  // (v. `asaza` din StepFem): desenat DUPĂ ele, le-ar da un voal peste cifre.
  grup.traverse((o) => { o.renderOrder = 9_000_000; });
  return grup;
}

/** Golește grupul cotelor și eliberează ce ține el pe placă: fiecare cotă are
 *  geometriile ei și un text rasterizat pe o pânză proprie, iar selecția se
 *  schimbă la fiecare click. */
export function elibereazaCote(grup: THREE.Group) {
  grup.traverse((o) => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose?.();
    const mat = m.material as THREE.Material & { map?: THREE.Texture };
    if (!mat) return;
    (Array.isArray(mat) ? mat : [mat]).forEach(
      (x: THREE.Material & { map?: THREE.Texture }) => {
        x.map?.dispose();
        x.dispose();
      });
  });
  grup.clear();
}
